import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { authService } from './authService';
import { getGlobalLogoutHandler } from '../context/AuthContext';

// Create axios instance
const axiosInstance: AxiosInstance = axios.create();

// Request interceptor to add JWT token and check expiration
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const currentUser = authService.getCurrentUser();
    if (currentUser && currentUser.token) {
      // Check if token is expired before making the request
      if (authService.isTokenExpired(currentUser.token)) {
        const logoutHandler = getGlobalLogoutHandler();
        if (logoutHandler) {
          logoutHandler('Your session has timed out. Please log in again.');
        } else {
          authService.logout();
          window.location.href = '/login';
        }
        return Promise.reject(new Error('Token expired'));
      }
      config.headers.Authorization = `Bearer ${currentUser.token}`;
      console.log('Adding JWT token to request:', config.url);
    } else {
      console.log('No JWT token found for request:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      // Handle 401 Unauthorized (JWT expired or invalid)
      if (error.response.status === 401) {
        const logoutHandler = getGlobalLogoutHandler();
        if (logoutHandler) {
          logoutHandler('Your session has timed out. Please log in again.');
        } else {
          authService.logout();
          window.location.href = '/login';
        }
        return Promise.reject(new Error('Session expired'));
      }

      // Return error message from response
      const errorMessage = (error.response.data as any)?.message || error.response.statusText || 'An error occurred';
      return Promise.reject(errorMessage);
    }

    // Handle network errors
    if (error.request) {
      return Promise.reject('Network error. Please check your connection.');
    }

    return Promise.reject(error.message || 'An error occurred');
  }
);

export default axiosInstance;
